import { lazy, Suspense, useEffect } from "react";
import type { ComponentType } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

// ── eager: 하단 5탭 루트 + 인증 진입 화면 (첫 로드 필수) ──────────────────
import { HomePage } from "../pages/HomePage";            // 탭 "/"
import { FinancePage } from "../pages/FinancePage";      // "/finance" — 2026-09-07부터 탭이 아니라 MY 하위 "자산·성향 도구"(라우트·리다이렉트 3건은 유지)
import { GamesPage } from "../pages/GamesPage";          // 탭 "/games" (모의투자 루트, 게임 아님)
import { CommunityPage } from "../pages/CommunityPage";  // 탭 "/community"
import { MyPage } from "../pages/MyPage";                // 탭 "/my"
import { LoginPage } from "../pages/LoginPage";          // 인증 게이트
import { OnboardingPage } from "../pages/OnboardingPage";// 신규 진입
import { ToastHost } from "../components/ToastHost";
import { RouteFallback } from "../components/common/RouteFallback"; // lazy 화면 도착 전 골격(빈 화면 방지)
import SwipeBack from "../components/SwipeBack"; // P2 §30 전역 좌측 엣지 스와이프 뒤로가기
import AndroidBackButton from "../components/AndroidBackButton"; // 안드로이드 시스템 뒤로 버튼 (2026-09-11 v5)
import HistoryProbe, { HISTORY_PROBE_ON } from "../components/HistoryProbe"; // B-1 계측(VITE_HISTORY_DEBUG=1 빌드에서만)
import { attachDeepLinkListener } from "../lib/deeplink";
import { useNotificationOutbox } from "../hooks/useNotificationOutbox";
import { useAuth } from "../lib/useAuth";
import { initializeIap, processPendingTransactions, refreshIapCatalog } from "../lib/iap";
import { redesignRoutes } from "./redesign";
import { LegacyMyIndicatorsRedirect } from "../redesign/live/studio/studioRoutes";

// ── H-2 코드 스플리팅 (2026-07-05 코드감사) ──────────────────────────────
// 비탭 화면(상세·설정·시그니처·가챠·차트·히트맵·포트분석 등)은 lazy 로 분리해
// 초기 번들에서 제외 → 첫 로드 TTI 개선. 상위 <Suspense> 가 fallback 을 커버.
// pages 는 named export 라 { default } 로 매핑. import 경로는 리터럴이어야
// Vite 가 청크를 정적 분리한다.
const lazyNamed = <T extends string>(
  factory: () => Promise<Record<T, ComponentType<any>>>,
  name: T,
) => lazy(() => factory().then((m) => ({ default: m[name] })));

const BudgetPage = lazyNamed(() => import("../pages/BudgetPage"), "BudgetPage");
const AssetPage = lazyNamed(() => import("../pages/AssetPage"), "AssetPage");
const MbtiPage = lazyNamed(() => import("../pages/MbtiPage"), "MbtiPage");
const MbtiDiagnosePage = lazyNamed(() => import("../pages/MbtiDiagnosePage"), "MbtiDiagnosePage");
const CsvImportPage = lazyNamed(() => import("../pages/CsvImportPage"), "CsvImportPage");
const WritePage = lazyNamed(() => import("../pages/WritePage"), "WritePage");
const PostDetailPage = lazyNamed(() => import("../pages/PostDetailPage"), "PostDetailPage");
const IndicatorDetailScreen = lazyNamed(() => import("../redesign/live/IndicatorDetailScreen"), "IndicatorDetailScreen");
const AccountLinkPage = lazyNamed(() => import("../pages/AccountLinkPage"), "AccountLinkPage");
const AuthCallbackPage = lazyNamed(() => import("../pages/AuthCallbackPage"), "AuthCallbackPage");
const GoalsPage = lazyNamed(() => import("../pages/GoalsPage"), "GoalsPage");
const AboutPage = lazyNamed(() => import("../pages/AboutPage"), "AboutPage");
const RiskPage = lazyNamed(() => import("../pages/RiskPage"), "RiskPage");
const PlayPage = lazyNamed(() => import("../pages/PlayPage"), "PlayPage");
const PlayChoicePage = lazyNamed(() => import("../pages/PlayChoicePage"), "PlayChoicePage");
const PlayResultPage = lazyNamed(() => import("../pages/PlayResultPage"), "PlayResultPage");
const FuturePricePage = lazyNamed(() => import("../pages/FuturePricePage"), "FuturePricePage"); // 미래가격 계산기 (6엔진 Step 3)
const MacroEventPage = lazyNamed(() => import("../pages/MacroEventPage"), "MacroEventPage"); // 거시 이벤트방 (Step 4)
const TurnaroundRadarPage = lazyNamed(() => import("../pages/TurnaroundRadarPage"), "TurnaroundRadarPage"); // 소외주 레이더 (스크리너 D §7)
const BuyOrNotPage = lazyNamed(() => import("../pages/BuyOrNotPage"), "BuyOrNotPage"); // 살까말까 (Step 5)
const ChallengeBoardPage = lazyNamed(() => import("../pages/ChallengeBoardPage"), "ChallengeBoardPage"); // 챌린지 시즌 (Step 6)
const HeatmapPage = lazyNamed(() => import("../pages/HeatmapPage"), "HeatmapPage");
const SimPage = lazyNamed(() => import("../pages/SimPage"), "SimPage"); // 실시간 모의투자 (§14)
const ReflectPage = lazyNamed(() => import("../pages/ReflectPage"), "ReflectPage"); // 복기 풀루프 (§5)
const FailMuseumPage = lazyNamed(() => import("../pages/FailMuseumPage"), "FailMuseumPage"); // 실패박물관 (§5 ⑥)
const BragHallPage = lazyNamed(() => import("../pages/BragHallPage"), "BragHallPage"); // 자랑 홀 (§6 ⑥)
const StockDetailPage = lazyNamed(() => import("../pages/StockDetailPage"), "StockDetailPage"); // 증권사식 차트+지표 (§17)
const IndicatorStudioPage = lazyNamed(() => import("../pages/IndicatorStudioPage"), "IndicatorStudioPage"); // 지표 스튜디오 — 수식 리스트 편집기 (v2)
const IndicatorStudioEditPage = lazyNamed(() => import("../pages/IndicatorStudioPage"), "IndicatorStudioEditPage");
const DiscoverPage = lazyNamed(() => import("../pages/DiscoverPage"), "DiscoverPage"); // 종목 발굴 (§17 큐C)
const PortfolioAnalysisPage = lazyNamed(() => import("../pages/PortfolioAnalysisPage"), "PortfolioAnalysisPage"); // 포트 분석 (§17 큐D)
const DebateReviewPage = lazyNamed(() => import("../pages/DebateReviewPage"), "DebateReviewPage"); // AI 토론 복기 (2026-06-16)
const ReviewHistoryPage = lazyNamed(() => import("../pages/ReviewHistoryPage"), "ReviewHistoryPage"); // AI 복기 히스토리 (2026-06-20)
const AlertsPage = lazyNamed(() => import("../pages/AlertsPage"), "AlertsPage"); // 알림 인박스 (§18-I)
const MessagesPage = lazyNamed(() => import("../pages/MessagesPage"), "MessagesPage"); // 시스템 쪽지 받은편지함
const ComparePage = lazyNamed(() => import("../pages/ComparePage"), "ComparePage"); // 종목 비교 (§18-L)
const LeaderboardPage = lazyNamed(() => import("../pages/LeaderboardPage"), "LeaderboardPage"); // 리더보드 (§18-M)
const ScrapPage = lazyNamed(() => import("../pages/ScrapPage"), "ScrapPage"); // 폐지수집소 (§16-2)
const CollectionPage = lazyNamed(() => import("../pages/CollectionPage"), "CollectionPage"); // 황소 컬렉션
// ⚠ Screen이 아니라 Page(=TabLayout 래퍼)를 건다 — 스크롤 컨테이너가 거기 있다.
const StorePage = lazyNamed(() => import("../pages/StorePage"), "StorePage");
const TopupHistoryPage = lazyNamed(() => import("../pages/TopupHistoryPage"), "TopupHistoryPage");
const ReviewAutoPage = lazyNamed(() => import("../pages/ReviewAutoPage"), "ReviewAutoPage"); // 자동 복기·자동 토론 설정 (2026-08-28)
const SubscribePage = lazyNamed(() => import("../pages/SubscribePage"), "SubscribePage");
const SubscriptionNoticePage = lazyNamed(() => import("../pages/SubscriptionNoticePage"), "SubscriptionNoticePage");
const MotionLabScreen = lazyNamed(() => import("../redesign/live/MotionLabScreen"), "MotionLabScreen"); // ⚠ 임시 검수실
const DesignLabScreen = lazyNamed(() => import("../redesign/live/DesignLabScreen"), "DesignLabScreen"); // ⚠ 임시 디자인 기법 검수실
// 코스메틱 4화면 — 한 파일(CosmeticPages) 내 named export. 동일 파일 dynamic import 는 한 청크로 공유됨.
const GachaPage = lazyNamed(() => import("../pages/CosmeticPages"), "GachaPage");
const CardCollectionPage = lazyNamed(() => import("../pages/CosmeticPages"), "CardCollectionPage");
const EnhancePage = lazyNamed(() => import("../pages/CosmeticPages"), "EnhancePage");
const HallPage = lazyNamed(() => import("../pages/CosmeticPages"), "HallPage");
// ⚠ 시그니처 14종(SignatureC1~14Page)은 Step 0(2026-06-18) 전체 숨김 → import 자체 제거됨(라우트는 /finance 리다이렉트).
// ⚠ 게임 시스템(차트게임·TOTO 등)은 2026-06-14 v1 스코프 좁히기로 _archive 은퇴.

// Codex 권고: BrowserRouter 기본, 네이티브 file:// 환경에선 HashRouter fallback
// Capacitor.isNativePlatform() true = iOS/Android 앱 안 → HashRouter 안전
const Router = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

function NotificationWatcher() {
  useNotificationOutbox();
  return null;
}

export function AppRoutes() {
  const { user } = useAuth();
  // Step 8.3 — OAuth deep link 리스너를 앱 진입 시 1회 부착
  // (web에서는 isNativePlatform false라 attachDeepLinkListener 자체가 no-op)
  useEffect(() => {
    attachDeepLinkListener();
  }, []);

  // 스토어 연결은 로그인과 무관하게 앱 부팅 1회 시작한다. 로그인 직후에는 미완료 거래를
  // 다시 검증해, 결제 중 네트워크가 끊긴 사용자의 재화가 누락되지 않게 한다.
  useEffect(() => {
    void initializeIap();
  }, []);
  useEffect(() => {
    if (!user?.id) return;
    // 부팅 때 받은 카탈로그는 미로그인 기준이라 첫 충전 안내가 0이다 — 로그인 후 다시 받는다.
    void initializeIap()
      .then(() => refreshIapCatalog())
      .then(() => processPendingTransactions());
  }, [user?.id]);

  return (
    <Router>
      {/* P2 §30 — 스와이프 백 인터랙티브 트랜지션 대상(SwipeBack이 transform). 토스트/제스처 레이어는 밖에 둔다. */}
      <div id="screen-stage">
      {/* H-2 코드 스플리팅 — lazy 화면 로드 중 fallback.
          ⚠ 2026-08-28: 종전 주석 "네이티브 로컬 청크라 통상 순간적"을 근거로 빈 div를 뒀는데,
          그 전제가 안 맞는 자리가 있었다(청크 미캐시·저사양). lazy 49개 화면 전부가 이 구간에서
          백지로 보였다 → 빈 div 대신 골격(RouteFallback). */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/asset" element={<AssetPage />} />
        <Route path="/mbti" element={<MbtiPage />} />
        <Route path="/mbti/diagnose" element={<MbtiDiagnosePage />} />
        {/* ⚠ 시그니처 14종(+단수 /signature) — 정적 하드코딩 데모 전체 숨김 (Step 0, 2026-06-18).
            14종→6엔진 재설계 (design doc 2026-06-18-signature-redesign-community-engine-design.md).
            진짜 6엔진은 Step 1~6에서 새 위치(금융 HERO·커뮤니티·거래 직후 등)에 배선.
            직접 URL/딥링크 접근 차단 → 금융탭으로 리다이렉트. 화면 파일은 보존(삭제는 출시 후). */}
        <Route path="/signature" element={<Navigate to="/finance" replace />} />
        <Route path="/signature/*" element={<Navigate to="/finance" replace />} />
        {/* 옛 개별 라우트(/signature/mbti-full ~ /signature/rate-alert 14종)는 위 wildcard가 흡수 */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* sotda.com 루트 통합 시 /auth/callback은 Next의 Google PKCE 핸들러가 점유한다
            → SPA 콜백은 이 경로로 비켜 준다 (설계 2026-07-28-spa-root-domain-design §4-1) */}
        <Route path="/auth/spa-callback" element={<AuthCallbackPage />} />
        <Route path="/csv-import" element={<CsvImportPage />} />
        {/* Phase A2·B (design doc 2026-05-26-finance-to-sotda-design-mapping §7) — finance/* 영입 */}
        <Route path="/goals" element={<GoalsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/risk" element={<RiskPage />} />{/* B 1️⃣ — MBTI 자매 진단 */}
        <Route path="/play" element={<PlayPage />} />{/* B 2️⃣ — 자산 시뮬 Step 1 */}
        <Route path="/play/choice" element={<PlayChoicePage />} />{/* Step 2 입력 선택 */}
        <Route path="/play/result" element={<PlayResultPage />} />{/* Step 3 결과 */}
        <Route path="/future-price" element={<FuturePricePage />} />{/* 미래가격 계산기 (6엔진 Step 3) */}
        {/* 검수실 2개 = 개발 빌드 전용(2026-09-03) — 금융탭 링크와 함께 라우트도 막아 딥링크로도 prod에서 열리지 않게 */}
        {import.meta.env.DEV && <Route path="/motion-lab" element={<MotionLabScreen />} />}
        {import.meta.env.DEV && <Route path="/design-lab" element={<DesignLabScreen />} />}
        <Route path="/turnaround-radar" element={<TurnaroundRadarPage />} />{/* 소외주 레이더 — 리포트 폭증 스캔 (스크리너 D §7) */}
        <Route path="/macro-event" element={<MacroEventPage />} />{/* 거시 이벤트방 활성 목록 (6엔진 Step 4) */}
        <Route path="/macro-event/:id" element={<MacroEventPage />} />{/* 거시 이벤트방 단건 투표 */}
        <Route path="/buy-or-not" element={<BuyOrNotPage />} />{/* 살까말까 결정방 목록·2탭 (6엔진 Step 5) */}
        <Route path="/buy-or-not/:id" element={<BuyOrNotPage />} />{/* 살까말까 단건 결정 */}
        <Route path="/challenge-board" element={<ChallengeBoardPage />} />{/* 행동 챌린지 시즌 랭킹 (6엔진 Step 6) */}
        <Route path="/signatures" element={<Navigate to="/finance" replace />} />{/* ⚠ 14종 Hub 숨김 (Step 0, 2026-06-18) — 금융탭 리다이렉트 */}
        <Route path="/heatmap" element={<HeatmapPage />} />{/* B 6️⃣ — 11 섹터 placeholder (squarify treemap = Phase C) */}
        <Route path="/sim" element={<SimPage />} />{/* 실시간 모의투자 — BULL 가상자금 운용 (design doc §14) */}
        <Route path="/sim/reflect" element={<ReflectPage />} />{/* 실패박물관 복기 풀루프 — 고백→판결→처방 (통합 design doc §5) */}
        <Route path="/failmuseum" element={<FailMuseumPage />} />{/* 실패박물관 전시실 — 소장 카드 진화 (§5 ⑥) */}
        <Route path="/failmuseum/brag" element={<BragHallPage />} />{/* 자랑(부기) 홀 — 5계층 (§6 ⑥) */}
        <Route path="/stock/:symbol" element={<StockDetailPage />} />{/* 종목 상세 — 캔들 차트+기술지표 (design doc §17) */}
        {/* /chart-settings 폐기 (2026-07-19 Phase 1b) — 지표 설정은 종목상세 차트 설정 시트의 2단으로 흡수 */}
        <Route path="/indicator-studio" element={<IndicatorStudioPage />} />{/* 지표 스튜디오 — 팔레트 홈 */}
        <Route path="/indicator-studio/edit" element={<IndicatorStudioEditPage />} />{/* 지표 스튜디오 편집기 */}
        <Route path="/discover" element={<DiscoverPage />} />{/* 종목 발굴 — 워치/등락순위/스크리너 (§17 큐C) */}
        <Route path="/alerts" element={<AlertsPage />} />{/* 가격/지표 알림 인박스 (§18-I 큐I) */}
        <Route path="/messages" element={<MessagesPage />} />{/* 시스템 쪽지 받은편지함 */}
        <Route path="/messages/:id" element={<MessagesPage />} />{/* 시스템 쪽지 상세 */}
        <Route path="/compare" element={<ComparePage />} />{/* 종목 비교 — 정규화 라인 (§18-L 큐L) */}
        <Route path="/leaderboard" element={<LeaderboardPage />} />{/* 랭킹 — 알파순 리더보드 (§18-M 큐M) */}
        <Route path="/portfolio-analysis" element={<PortfolioAnalysisPage />} />{/* 포트 분석 — 추이/도넛/매매일지 (§17 큐D) */}
        <Route path="/debate-review" element={<DebateReviewPage />} />{/* AI 토론 복기 히스토리 (2026-06-16) */}
        <Route path="/debate-review/:id" element={<DebateReviewPage />} />{/* AI 토론 복기 전체화면 (2026-06-16) */}
        <Route path="/review-history" element={<ReviewHistoryPage />} />{/* AI 복기(일반) 히스토리 목록 (2026-06-20) */}
        <Route path="/review-history/:id" element={<ReviewHistoryPage />} />{/* AI 복기(일반) 전체화면 (2026-06-20) */}
        <Route path="/review-auto" element={<ReviewAutoPage />} />{/* 자동 복기·자동 토론 설정 (2026-08-28) */}
        <Route path="/scrap" element={<ScrapPage />} />{/* 폐지수집소 — 파산 구제 (design doc §16-2) */}
        {/* 코스메틱 경제 (cosmetic-economy-spec §3~§10·§14) — cash_bull 전용·투자 무관 치장 */}
        <Route path="/gacha" element={<GachaPage />} />{/* 카드 뽑기 — 확률 공개 §12 */}
        <Route path="/cards" element={<CardCollectionPage />} />{/* 카드 컬렉션 44종+배지 14 (⚠ /collection=황소 컬렉션과 별개) */}
        <Route path="/enhance/:cardId" element={<EnhancePage />} />{/* 카드 강화 — 천장·보호권 §3~§5 */}
        <Route path="/hall" element={<HallPage />} />{/* 공시관 — 치장 보드 §10 (실력 랭킹과 분리) */}
        {/* IA 재편 (design doc 2026-05-22-sotda-ia-restructure §3) — 새 5탭 */}
        <Route path="/finance" element={<FinancePage />} />
        <Route path="/games" element={<GamesPage />} />{/* 모의투자 탭 루트 — SimScreen (게임 시스템은 2026-06-14 _archive 은퇴) */}
        <Route path="/collection" element={<CollectionPage />} />{/* 황소 컬렉션 */}
        <Route path="/store" element={<StorePage />} />{/* IAP 크레딧 팩 6종 */}
        <Route path="/store/history" element={<TopupHistoryPage />} />{/* 유료 충전 내역 (bull_topup_logs) */}
        <Route path="/subscribe" element={<SubscribePage />} />{/* IAP 구독 2티어 × 월/연 */}
        <Route path="/subscribe/notice" element={<SubscriptionNoticePage />} />{/* 자동 갱신·해지 고지 전문 */}
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/community/write" element={<WritePage />} />
        <Route path="/community/post/:id/edit" element={<WritePage />} />
        <Route path="/community/post/:id" element={<PostDetailPage />} />
        <Route path="/community/indicator/:versionId" element={<IndicatorDetailScreen />} />
        <Route path="/community/my-indicators" element={<LegacyMyIndicatorsRedirect />} />
        <Route path="/my" element={<MyPage />} />
        <Route path="/account-link" element={<AccountLinkPage />} />{/* 계정 연동 — MY 자식 (design doc 2026-06-16 §6) */}
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* choishin 리디자인 프리뷰 (정적 시안 · 검수용 · 기존 화면 무영향) */}
        {redesignRoutes}
        {/* 404 fallback — 홈으로 */}
        <Route path="*" element={<HomePage />} />
      </Routes>
      </Suspense>
      </div>
      <ToastHost />
      <NotificationWatcher />
      <SwipeBack />
      <AndroidBackButton />
      {HISTORY_PROBE_ON && <HistoryProbe />}
    </Router>
  );
}
